<script setup lang="ts">
import { computed } from "vue";

type BadgeSize = "sm" | "md" | "lg";

type Props = {
  count?: number;
  size?: BadgeSize;
  position?: "static" | "absolute";
  top?: string | number;
  right?: string | number;
  showCount?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  count: 0,
  size: "md",
  position: "static",
  top: "0",
  right: "0",
  showCount: true,
});

const sizeClasses = computed((): string => {
  switch (props.size) {
    case "sm":
      return "notification-badge--sm";
    case "lg":
      return "notification-badge--lg";
    default:
      return "";
  }
});

const positionClass = computed((): string => {
  return props.position === "absolute" ? "position-absolute" : "";
});

const style = computed((): Record<string, string> => {
  if (props.position === "absolute") {
    return {
      top: typeof props.top === "number" ? `${props.top}px` : props.top,
      right: typeof props.right === "number" ? `${props.right}px` : props.right,
    };
  }

  return {};
});

const isVisible = computed((): boolean => props.count > 0);
</script>

<template>
  <span
    v-if="isVisible"
    class="notification-badge"
    :class="[sizeClasses, positionClass]"
    :style="style"
  >
    <span v-if="showCount">{{ count }}</span>
  </span>
</template>

<style lang="scss" scoped>
.notification-badge {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: $brand-3;
  color: white;
  font-weight: bold;
  line-height: 1;
  width: 16px;
  height: 16px;
  font-size: 10px;

  &--sm {
    width: 11px;
    height: 11px;
    font-size: 6px;
  }

  &--lg {
    width: 20px;
    height: 20px;
    font-size: 12px;
  }
}
</style>
